import React from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FloppyDisk,
  ArrowsClockwise,
  Eye,
  Ruler,
  ArrowUUpLeft,
  ArrowUUpRight,
  DownloadSimple,
  UploadSimple,
  GridFour,
  TextT,
  Rectangle,
  LineSegment,
} from "@phosphor-icons/react";
import { GRID_OPTIONS } from "./constants";

/**
 * Header toolbar for the Layout Editor: title + all action buttons
 * (undo/redo, export/import, live preview toggle, grid, paper, add shapes,
 * reset, save). Pure presentational — all handlers wired from parent.
 */
export default function LayoutToolbar({
  history,
  onUndo,
  onRedo,
  onExport,
  onImportClick,
  onImportFile,
  importInputRef,
  previewMode,
  setPreviewMode,
  gridStep,
  setGridStep,
  pageCfg,
  updatePageCfg,
  onAddCustomText,
  onAddRectangle,
  onAddLine,
  onReset,
  onSave,
  saving,
}) {
  return (
    <div className="border-b border-border pb-4 mb-4 flex flex-col md:flex-row md:items-end justify-between gap-3">
      <div>
        <div className="overline">Desain Cetak</div>
        <h1 className="text-2xl md:text-3xl font-black tracking-tight mt-1 flex items-center gap-2">
          <Ruler size={26} weight="bold" /> Layout Editor SPB
        </h1>
        <p className="text-muted-foreground text-xs mt-1">
          Drag setiap field pada preview A5 di bawah. Klik field untuk edit ukuran font, bold, atau sembunyikan. Simpan untuk pakai di seluruh SPB.
        </p>
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <div className="flex border border-border">
          <button
            onClick={onUndo}
            disabled={!history.canUndo}
            className="px-3 h-9 border-r border-border disabled:opacity-40 hover:bg-secondary flex items-center gap-1 text-xs font-semibold"
            title="Undo (Ctrl+Z)"
            data-testid="undo-button"
          >
            <ArrowUUpLeft size={14} weight="bold" /> Undo
          </button>
          <button
            onClick={onRedo}
            disabled={!history.canRedo}
            className="px-3 h-9 disabled:opacity-40 hover:bg-secondary flex items-center gap-1 text-xs font-semibold"
            title="Redo (Ctrl+Shift+Z)"
            data-testid="redo-button"
          >
            <ArrowUUpRight size={14} weight="bold" /> Redo
          </button>
        </div>
        <div className="flex border border-border">
          <button
            onClick={onExport}
            className="px-3 h-9 border-r border-border hover:bg-secondary flex items-center gap-1 text-xs font-semibold"
            title="Export layout ke JSON"
            data-testid="export-layout-button"
          >
            <DownloadSimple size={14} weight="bold" /> Export
          </button>
          <button
            onClick={onImportClick}
            className="px-3 h-9 hover:bg-secondary flex items-center gap-1 text-xs font-semibold"
            title="Import dari file JSON"
            data-testid="import-layout-button"
          >
            <UploadSimple size={14} weight="bold" /> Import
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            onChange={onImportFile}
            className="hidden"
            data-testid="import-layout-input"
          />
        </div>
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={() => setPreviewMode(!previewMode)}
          data-testid="toggle-preview-button"
        >
          <Eye size={16} className="mr-2" /> {previewMode ? "Mode Edit" : "Live PDF"}
        </Button>
        <div className="flex items-center border border-border h-9 pl-2">
          <GridFour size={14} weight="bold" className="mr-1 text-muted-foreground" />
          <Select value={String(gridStep)} onValueChange={(v) => setGridStep(parseFloat(v))}>
            <SelectTrigger className="border-0 h-9 w-24 rounded-xl text-xs font-mono" data-testid="grid-snap-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GRID_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={String(o.value)}>
                  Snap · {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex border border-border h-9">
          <Select value={pageCfg.size} onValueChange={(v) => updatePageCfg({ size: v })}>
            <SelectTrigger className="border-0 h-9 w-20 rounded-xl text-xs font-mono" data-testid="page-size-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="A5">A5</SelectItem>
              <SelectItem value="A4">A4</SelectItem>
              <SelectItem value="LETTER">Letter</SelectItem>
            </SelectContent>
          </Select>
          <div className="border-l border-border" />
          <Select value={pageCfg.orientation} onValueChange={(v) => updatePageCfg({ orientation: v })}>
            <SelectTrigger className="border-0 h-9 w-32 rounded-xl text-xs" data-testid="page-orient-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="landscape">Landscape</SelectItem>
              <SelectItem value="portrait">Portrait</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={onAddCustomText}
          data-testid="add-custom-text-button"
          title="Tambah teks kustom bebas"
        >
          <TextT size={16} className="mr-2" weight="bold" /> + Teks
        </Button>
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={onAddRectangle}
          data-testid="add-rectangle-button"
          title="Tambah kotak / border"
        >
          <Rectangle size={16} className="mr-2" weight="bold" /> + Kotak
        </Button>
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={onAddLine}
          data-testid="add-line-button"
          title="Tambah garis horizontal"
        >
          <LineSegment size={16} className="mr-2" weight="bold" /> + Garis
        </Button>
        <Button
          variant="outline"
          className="rounded-xl text-destructive"
          onClick={onReset}
          data-testid="reset-layout-button"
        >
          <ArrowsClockwise size={16} className="mr-2" /> Reset Default
        </Button>
        <Button
          onClick={onSave}
          disabled={saving}
          className="rounded-xl font-bold uppercase tracking-wider"
          data-testid="save-layout-button"
        >
          <FloppyDisk size={16} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan Layout"}
        </Button>
      </div>
    </div>
  );
}
